import { Injectable, computed, inject } from '@angular/core';
import { PLATFORM_DASHBOARD_PREVIEW_LIMIT } from '../models/platform.models';
import { PlatformApplicationStatus } from '../enums/platform-application-status.enum';
import { PlatformAuthService } from './platform-auth.service';
import { PlatformJobsService } from './platform-jobs.service';
import { PlatformProfileService } from './platform-profile.service';

@Injectable({
  providedIn: 'root',
})
export class PlatformDashboardService {
  private readonly auth = inject(PlatformAuthService);
  private readonly profile = inject(PlatformProfileService);
  private readonly jobs = inject(PlatformJobsService);

  readonly hasJobRecommendations = computed(
    () => this.jobs.recommendedJobs().length > 0
  );
  readonly previewLimit = PLATFORM_DASHBOARD_PREVIEW_LIMIT;
  readonly user = computed(() => this.auth.displayUser());
  readonly actionItems = computed(() => this.jobs.actionItems());
  readonly invitations = computed(() => this.jobs.invitations());
  readonly applications = computed(() => this.jobs.applications());
  readonly previewInvitations = computed(() =>
    this.invitations().slice(0, this.previewLimit)
  );
  readonly previewApplications = computed(() =>
    this.applications().slice(0, this.previewLimit)
  );
  readonly showProfileCompleteness = computed(() => this.profile.showProfileCompleteness());
  readonly isDashboardEmpty = computed(
    () =>
      this.actionItems().length === 0 &&
      this.invitations().length === 0 &&
      this.applications().length === 0
  );

  /**
   * Accepts an invitation by applying with the profile.
   * @param invitationId - Invitation id
   */
  acceptInvitation(invitationId: number): void {
    this.jobs.acceptInvitation(invitationId);
  }

  /**
   * Declines an invitation.
   * @param invitationId - Invitation id
   */
  declineInvitation(invitationId: number): void {
    this.jobs.declineInvitation(invitationId);
  }

  /**
   * Returns the transloco key for an application status.
   * @param status - Application tracking status
   * @returns Translation key
   */
  statusTranslationKey(status: PlatformApplicationStatus): string {
    return this.jobs.statusTranslationKey(status);
  }

  /**
   * Returns the transloco key for a missing-data type.
   * @param type - Missing data type
   * @returns Translation key
   */
  missingDataTranslationKey(type: string): string {
    return this.jobs.missingDataTranslationKey(type);
  }
}
